import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicNav from "@/components/layout/PublicNav";
import Footer from "@/components/layout/Footer";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Agencies from "@/pages/Agencies";
import AgencyProfile from "@/pages/AgencyProfile";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import { HowItWorks, ForAgencies, ForBusinesses, About, Contact, FAQ, Legal } from "@/pages/Marketing";

import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import PostRequirement from "@/pages/customer/PostRequirement";
import Requirements from "@/pages/customer/Requirements";
import RequirementDetail from "@/pages/customer/RequirementDetail";
import Contracts from "@/pages/customer/Contracts";
import ContractDetail from "@/pages/customer/ContractDetail";
import Invoices from "@/pages/customer/Invoices";
import Incidents from "@/pages/customer/Incidents";
import Replacements from "@/pages/customer/Replacements";

import AgencyDashboard from "@/pages/agency/AgencyDashboard";
import Leads from "@/pages/agency/Leads";
import AgencyQuotes from "@/pages/agency/AgencyQuotes";
import Guards from "@/pages/agency/Guards";
import AgencyContracts from "@/pages/agency/AgencyContracts";
import AgencyInvoices from "@/pages/agency/AgencyInvoices";
import AgencyProfileEdit from "@/pages/agency/AgencyProfileEdit";
import AgencyMyLeads from "@/pages/agency/AgencyMyLeads";
import AgencyRegister from "@/pages/agency/AgencyRegister";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminAgencies from "@/pages/admin/AdminAgencies";
import AdminAgencyDetail from "@/pages/admin/AdminAgencyDetail";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAboutEdit from "@/pages/admin/AdminAboutEdit";
import AdminContactEdit from "@/pages/admin/AdminContactEdit";
import AdminFAQ from "@/pages/admin/AdminFAQ";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminReports from "@/pages/admin/AdminReports";
import AdminLeadManagement from "@/pages/admin/AdminLeadManagement";

function Public({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Public><Home /></Public>} />
      <Route path="/services" element={<Public><Services /></Public>} />
      <Route path="/agencies" element={<Public><Agencies /></Public>} />
      <Route path="/agencies/:id" element={<Public><AgencyProfile /></Public>} />
      <Route path="/pricing" element={<Public><Pricing /></Public>} />
      <Route path="/how-it-works" element={<Public><HowItWorks /></Public>} />
      <Route path="/for-agencies" element={<Public><ForAgencies /></Public>} />
      <Route path="/for-businesses" element={<Public><ForBusinesses /></Public>} />
      <Route path="/about" element={<Public><About /></Public>} />
      <Route path="/contact" element={<Public><Contact /></Public>} />
      <Route path="/faq" element={<Public><FAQ /></Public>} />
      <Route path="/legal/:doc" element={<Public><Legal /></Public>} />
      <Route path="/login" element={<Login />} />

      <Route path="/dashboard" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/post-requirement" element={<ProtectedRoute><PostRequirement /></ProtectedRoute>} />
      <Route path="/requirements" element={<ProtectedRoute><Requirements /></ProtectedRoute>} />
      <Route path="/requirements/:id" element={<ProtectedRoute><RequirementDetail /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><Contracts /></ProtectedRoute>} />
      <Route path="/contracts/:id" element={<ProtectedRoute><ContractDetail /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
      <Route path="/replacements" element={<ProtectedRoute><Replacements /></ProtectedRoute>} />

      <Route path="/agency" element={<ProtectedRoute><AgencyDashboard /></ProtectedRoute>} />
      <Route path="/agency/leads" element={<ProtectedRoute role="agency"><Leads /></ProtectedRoute>} />
      <Route path="/agency/register" element={<AgencyRegister />} />
      <Route path="/register-agency" element={<AgencyRegister />} />
      <Route path="/agency/my-leads" element={<ProtectedRoute role="agency"><AgencyMyLeads /></ProtectedRoute>} />
      <Route path="/agency/quotes" element={<ProtectedRoute role="agency"><AgencyQuotes /></ProtectedRoute>} />
      <Route path="/agency/guards" element={<ProtectedRoute role="agency"><Guards /></ProtectedRoute>} />
      <Route path="/agency/contracts" element={<ProtectedRoute role="agency"><AgencyContracts /></ProtectedRoute>} />
      <Route path="/agency/invoices" element={<ProtectedRoute role="agency"><AgencyInvoices /></ProtectedRoute>} />
      <Route path="/agency/profile" element={<ProtectedRoute role="agency"><AgencyProfileEdit /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/lead-management" element={<ProtectedRoute role={["admin", "lead_manager"]}><AdminLeadManagement /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute role="admin"><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/verification" element={<ProtectedRoute role="admin"><AdminVerification /></ProtectedRoute>} />
      <Route path="/admin/agencies" element={<ProtectedRoute role="admin"><AdminAgencies /></ProtectedRoute>} />
      <Route path="/admin/agencies/:id" element={<ProtectedRoute role="admin"><AdminAgencyDetail /></ProtectedRoute>} />
      <Route path="/admin/packages" element={<ProtectedRoute role="admin"><AdminPackages /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute role="admin"><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/enquiries" element={<ProtectedRoute role="admin"><AdminEnquiries /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute role="admin"><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/content/about" element={<ProtectedRoute role="admin"><AdminAboutEdit /></ProtectedRoute>} />
      <Route path="/admin/content/contact" element={<ProtectedRoute role="admin"><AdminContactEdit /></ProtectedRoute>} />
      <Route path="/admin/faqs" element={<ProtectedRoute role="admin"><AdminFAQ /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute role="admin"><AdminSettings /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
